// ? Кэширование значений ячеек для корректного отображения объединенных ячеек, чтобы не вычислять повторно при виртуализации
import { Cell, Row } from '@tanstack/vue-table';

import { TCachedVirtualRows, TTableData } from '../types/tableTypes';

export type TCellSpanCache = Map<string, number | null | undefined>;

type TCellSpanFlagsCache = Map<string, boolean>;

export type TCellsSpanCache = {
  /** Кэширует количество строк, которое объединяет ячейка (rowspan). Ключ: `${rowIndex}-${columnId}`. */
  rowspan: TCellSpanCache;
  /** Кэширует количество столбцов, которое объединяет ячейка (colspan). Ключ: `${rowIndex}-${columnId}`.*/
  colspan: TCellSpanCache;
  /** Флаг того, что данная ячейка скрыта из-за объединения по rowspan. Ключ: `${rowIndex}-${columnId}`.*/
  rowspanHideCell: TCellSpanFlagsCache;
  /** Флаг, что ячейка является последней в группе объединения по rowspan (например, для декорации границ). Ключ: `${rowIndex}-${columnId}`.*/
  rowspanLastInSpan: TCellSpanFlagsCache;
  /** Флаг того, что данная ячейка скрыта из-за объединения по colspan. Ключ: `${rowIndex}-${columnId}`.*/
  colspanHideCell: TCellSpanFlagsCache;
  /** Флаг, что ячейка является последней в группе объединения по colspan. Ключ: `${rowIndex}-${columnId}`.*/
  colspanLastInSpan: TCellSpanFlagsCache;
};

type TBuildCellsSpanCacheParams = {
  /** Строки */
  rows: Row<unknown>[];
  /** Флаг того, что это загрузка с пагинацией */
  isPaginationLoadStrategy: boolean;
  /** Количество строк, которые были кэшированы */
  prevCachedRowCount: number;
  /** Предыдущий кэш ячейки */
  prevCellsSpanCache: TCellsSpanCache;
};

type TBuildCellsSpanCacheResult = {
  /** Кэш ячейки */
  cellsSpanCache: TCellsSpanCache;
  /** Количество строк, которые были кэшированы */
  cellsSpanCachedRowCount: number;
};

type TBuildCellsSpanVisibilityCacheParams = {
  rows: Row<unknown>[];
  cellsSpanCache: TCellsSpanCache;
  fromRowIndex: number;
  toRowIndex: number;
};

export const SPAN_CACHE_OVERSCAN_ROWS = 20;

/** @returns `${rowIndex}-${columnId}` */
export const getCellSpanCacheKey = (rowIndex: number, columnId: string): string => `${rowIndex}-${columnId}`;

/** @returns Пустой кэш ячейки */
export const getCellSpanCacheInitValue = (): TCellsSpanCache => ({
  rowspan: new Map(),
  colspan: new Map(),
  rowspanHideCell: new Map(),
  rowspanLastInSpan: new Map(),
  colspanHideCell: new Map(),
  colspanLastInSpan: new Map(),
});

export const buildCellsSpanCacheForRows = ({
  rows,
  isPaginationLoadStrategy,
  prevCachedRowCount,
  prevCellsSpanCache,
}: TBuildCellsSpanCacheParams): TBuildCellsSpanCacheResult => {
  const rowCount = rows.length;

  if (!rowCount) {
    return {
      cellsSpanCache: getCellSpanCacheInitValue(),
      cellsSpanCachedRowCount: 0,
    };
  }

  const { cellsSpanCache, cellsSpanCachedRowCount } = buildCellsSpanValueCacheForRows({
    rows,
    isPaginationLoadStrategy,
    prevCachedRowCount,
    prevCellsSpanCache,
  });

  buildCellsSpanVisibilityCacheForRowRange({
    rows,
    cellsSpanCache,
    fromRowIndex: 0,
    toRowIndex: rowCount - 1,
  });

  return { cellsSpanCache, cellsSpanCachedRowCount };
};

export const buildCellsSpanValueCacheForRows = ({
  rows,
  isPaginationLoadStrategy,
  prevCachedRowCount,
  prevCellsSpanCache,
}: TBuildCellsSpanCacheParams): TBuildCellsSpanCacheResult => {
  const rowCount = rows.length;

  const canExtendSpanCache = isPaginationLoadStrategy && prevCachedRowCount > 0 && rowCount > prevCachedRowCount;
  const shouldRebuildSpanCache = !canExtendSpanCache || rowCount < prevCachedRowCount;
  const startRowIndex = shouldRebuildSpanCache ? 0 : prevCachedRowCount;

  const {
    rowspan: rowspanCache,
    colspan: colspanCache,
    rowspanHideCell,
    rowspanLastInSpan,
    colspanHideCell,
    colspanLastInSpan,
  } = shouldRebuildSpanCache ? getCellSpanCacheInitValue() : cloneCellsSpanMaps(prevCellsSpanCache);

  appendRowspanColspanValueCaches(rows, rowspanCache, colspanCache, startRowIndex);

  return {
    cellsSpanCache: {
      rowspan: rowspanCache,
      colspan: colspanCache,
      rowspanHideCell,
      rowspanLastInSpan,
      colspanHideCell,
      colspanLastInSpan,
    },
    cellsSpanCachedRowCount: rowCount,
  };
};

export const buildCellsSpanVisibilityCacheForRowRange = ({
  rows,
  cellsSpanCache,
  fromRowIndex,
  toRowIndex,
}: TBuildCellsSpanVisibilityCacheParams): void => {
  const rowCount = rows.length;

  if (!rowCount || fromRowIndex > toRowIndex) {
    return;
  }
  const safeFrom = Math.max(0, fromRowIndex);
  const safeTo = Math.min(rowCount - 1, toRowIndex);

  fillRowspanVisibilityCachesForRowRange(
    rows,
    cellsSpanCache.rowspan,
    cellsSpanCache.rowspanHideCell,
    cellsSpanCache.rowspanLastInSpan,
    safeFrom,
    safeTo,
  );

  fillColspanVisibilityCachesFromRowIndex(
    rows,
    cellsSpanCache.colspan,
    cellsSpanCache.colspanHideCell,
    cellsSpanCache.colspanLastInSpan,
    safeFrom,
    safeTo,
  );
};

export const getSpanVisibilityRowRange = (
  virtualRows: TCachedVirtualRows,
  rowCount: number,
  overscanRows = SPAN_CACHE_OVERSCAN_ROWS,
): { fromRowIndex: number; toRowIndex: number } | null => {
  if (!rowCount) return null;

  if (!virtualRows.length) {
    return {
      fromRowIndex: 0,
      toRowIndex: Math.min(rowCount - 1, overscanRows * 2),
    };
  }

  const firstIndex = virtualRows[0]?.index ?? 0;
  const lastIndex = virtualRows[virtualRows.length - 1]?.index ?? firstIndex;

  return {
    fromRowIndex: Math.max(0, firstIndex - overscanRows),
    toRowIndex: Math.min(rowCount - 1, lastIndex + overscanRows),
  };
};

const cloneCellsSpanMaps = (source: TCellsSpanCache): TCellsSpanCache => ({
  rowspan: new Map(source.rowspan),
  colspan: new Map(source.colspan),
  rowspanHideCell: new Map(source.rowspanHideCell),
  rowspanLastInSpan: new Map(source.rowspanLastInSpan),
  colspanHideCell: new Map(source.colspanHideCell),
  colspanLastInSpan: new Map(source.colspanLastInSpan),
});

const appendRowspanColspanValueCaches = (
  rows: Row<unknown>[],
  rowspanCache: TCellSpanCache,
  colspanCache: TCellSpanCache,
  startRowIndex: number,
) => {
  for (let rowIndex = startRowIndex; rowIndex < rows.length; rowIndex++) {
    const row = rows[rowIndex];

    for (const cell of row.getVisibleCells()) {
      const key = getCellSpanCacheKey(rowIndex, cell.column.id);

      const getRowspan = cell.column.columnDef.meta?.getRowspan;
      rowspanCache.set(key, getRowspan?.(row.original as TTableData));
      const getColspan = cell.column.columnDef.meta?.getColspan;
      colspanCache.set(key, getColspan?.(row.original as TTableData, cell.getValue()));
    }
  }
};

type TActiveRowspan = {
  startRowIndex: number;
  endRowIndex: number;
};

const fillRowspanVisibilityCachesForRowRange = (
  rows: Row<unknown>[],
  rowspanCache: TCellSpanCache,
  rowspanHideCell: TCellSpanFlagsCache,
  rowspanLastInSpan: TCellSpanFlagsCache,
  fromRowIndex: number,
  toRowIndex: number,
) => {
  const activeRowspanByColumn = new Map<string, TActiveRowspan>();

  for (let rowIndex = 0; rowIndex < fromRowIndex; rowIndex++) {
    updateActiveRowspansForRow(rows, rowspanCache, activeRowspanByColumn, rowIndex);
  }

  for (let rowIndex = fromRowIndex; rowIndex <= toRowIndex; rowIndex++) {
    const row = rows[rowIndex];

    for (const cell of row.getVisibleCells()) {
      const columnId = cell.column.id;
      const key = getCellSpanCacheKey(rowIndex, columnId);
      const rowspanAtCell = rowspanCache.get(key);
      const activeRowspan = activeRowspanByColumn.get(columnId);

      if (rowspanAtCell === 0) {
        rowspanHideCell.set(key, true);
        rowspanLastInSpan.set(key, false);
        continue;
      }

      const isCoveredByActiveSpan =
        activeRowspan != null && rowIndex > activeRowspan.startRowIndex && rowIndex <= activeRowspan.endRowIndex;

      rowspanHideCell.set(key, isCoveredByActiveSpan);
      rowspanLastInSpan.set(
        key,
        isCoveredByActiveSpan && activeRowspan != null && rowIndex === activeRowspan.endRowIndex,
      );
    }

    updateActiveRowspansForRow(rows, rowspanCache, activeRowspanByColumn, rowIndex);
  }
};

const updateActiveRowspansForRow = (
  rows: Row<unknown>[],
  rowspanCache: TCellSpanCache,
  activeRowspanByColumn: Map<string, TActiveRowspan>,
  rowIndex: number,
) => {
  const row = rows[rowIndex];

  for (const cell of row.getVisibleCells()) {
    const columnId = cell.column.id;
    const key = getCellSpanCacheKey(rowIndex, columnId);
    const rowspanAtCell = rowspanCache.get(key);

    if (rowspanAtCell != null && rowspanAtCell > 1) {
      activeRowspanByColumn.set(columnId, {
        startRowIndex: rowIndex,
        endRowIndex: rowIndex + rowspanAtCell - 1,
      });
    }
  }
};

const fillColspanVisibilityCachesFromRowIndex = (
  rows: Row<unknown>[],
  colspanCache: TCellSpanCache,
  colspanHideCell: TCellSpanFlagsCache,
  colspanLastInSpan: TCellSpanFlagsCache,
  startRowIndex: number,
  endRowIndex: number,
) => {
  for (let rowIndex = startRowIndex; rowIndex <= endRowIndex; rowIndex++) {
    const row = rows[rowIndex];
    const visibleCells = row.getVisibleCells();

    for (let cellIndex = 0; cellIndex < visibleCells.length; cellIndex++) {
      const cell = visibleCells[cellIndex];
      const key = getCellSpanCacheKey(rowIndex, cell.column.id);

      colspanLastInSpan.set(key, computeColspanLastForIndex(colspanCache, rowIndex, visibleCells, cellIndex));
      colspanHideCell.set(key, computeColspanHideForIndex(colspanCache, rowIndex, visibleCells, cellIndex));
    }
  }
};

const computeColspanLastForIndex = (
  colspanCache: TCellSpanCache,
  rowIndex: number,
  visibleCells: Cell<unknown, unknown>[],
  currentCellIndex: number,
): boolean => {
  for (let cellIndex = 0; cellIndex < currentCellIndex; cellIndex++) {
    const prevColspan = colspanCache.get(getCellSpanCacheKey(rowIndex, visibleCells[cellIndex].column.id));
    if (prevColspan == null || prevColspan <= 1) continue;

    const colspanEnd = cellIndex + prevColspan - 1;
    if (currentCellIndex === colspanEnd) return true;
    if (currentCellIndex < colspanEnd) return false;
  }

  return false;
};

const computeColspanHideForIndex = (
  colspanCache: TCellSpanCache,
  rowIndex: number,
  visibleCells: Cell<unknown, unknown>[],
  currentCellIndex: number,
): boolean => {
  for (let cellIndex = 0; cellIndex < currentCellIndex; cellIndex++) {
    const prevColspan = colspanCache.get(getCellSpanCacheKey(rowIndex, visibleCells[cellIndex].column.id));
    if (prevColspan == null || prevColspan <= 1) continue;

    const colspanEnd = cellIndex + prevColspan - 1;
    if (currentCellIndex <= colspanEnd) return true;
  }

  return false;
};
